import React from "react";

import { ApiRoutes } from "@/api/client.ts";
import { summarizeTransferModels } from "@/components/TransferStatusBadge";
import { DataIntegrityJobSummary, TransferSummary } from "@/types";

export const linkToTransfer = (transfer: TransferSummary): string => {
  const destinationId = transfer.destination_id;
  return `${ApiRoutes.DESTINATIONS}/${destinationId}${ApiRoutes.TRANSFERS}/${transfer.id}`;
};

export const linkToIntegrityJob = (job: DataIntegrityJobSummary): string | undefined => {
  const destinationId = job.destination_id;
  if (!destinationId) {
    return;
  }
  return `${ApiRoutes.DESTINATIONS}/${destinationId}/integrity/${job.id}`;
};

export const sumRowsCompared = (job: DataIntegrityJobSummary): number => {
  const results = job.data_integrity_results;
  return results.reduce((sum, result) => sum + result.total_rows_compared, 0) || 0;
};

export const summarizeIntegrityJob = (job: DataIntegrityJobSummary): React.JSX.Element => {
  const results = job.data_integrity_results;

  if (results.length === 0) {
    return <span className="font-mono text-gray-500">No models compared</span>;
  }

  const numHealthy = results?.filter((m) => !m.error && m.is_healthy).length || 0;
  const numUnhealthy = results?.filter((m) => m.error || !m.is_healthy).length || 0;

  return (
    <span className="flex justify-between space-x-3 font-mono">
      {numHealthy > 0 && (
        <span className="text-emerald-600">
          {numHealthy}/{results.length} healthy{" "}
        </span>
      )}
      {numUnhealthy > 0 && (
        <span className="text-red-600">
          {numUnhealthy}/{results.length} unhealthy
        </span>
      )}
    </span>
  );
};

export const summarizeTransfer = (transfer: TransferSummary): React.JSX.Element => {
  const parts: React.ReactNode[] = [];
  const summary = summarizeTransferModels(transfer);

  if (summary.succeeded > 0) {
    parts.push(
      <span key="succeeded" className="text-emerald-600">
        {summary.succeeded}/{summary.total} succeeded
      </span>,
    );
  }

  if (summary.failed > 0) {
    parts.push(
      <span key="failed" className="text-red-600">
        {summary.failed}/{summary.total} failed
      </span>,
    );
  }

  if (summary.pending > 0) {
    parts.push(
      <span key="pending" className="text-gray-500">
        {summary.pending}/{summary.total} {transfer.status.toLowerCase()}
      </span>,
    );
  }

  return <span className="flex justify-between space-x-3 font-mono">{parts}</span>;
};
